import { Head, Link } from "@inertiajs/react"
import { Section as ContentSection } from "@/components/legal/Section"
import { MarketingFooter } from "@/components/marketing-footer"

import { useFlashcardProps } from "@/hooks/use-flashcard-props"

interface TermsOfServiceProps {
  lastUpdated?: string
  canonicalUrl?: string
}

export default function TermsOfService(props: any) {
  const { lastUpdated, canonicalUrl } =
    useFlashcardProps<TermsOfServiceProps>(props)

  return (
    <>
      <Head>
        <title>Terms of Service | ThaiCopilot</title>
        <meta
          name="description"
          content="Terms and conditions for using ThaiCopilot. By creating an account, you agree to these terms."
        />
        <meta name="robots" content="index, follow" />
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      </Head>

      {/* Hero Section */}
      <div className="border-b bg-gradient-to-br from-slate-50 to-white dark:from-slate-900 dark:to-slate-950">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="mb-6">
            <nav className="text-muted-foreground flex items-center space-x-2 text-sm">
              <Link
                href="/"
                className="transition-colors hover:text-indigo-600 dark:hover:text-indigo-400"
              >
                Home
              </Link>
              <span>/</span>
              <span className="text-foreground font-medium">
                Terms of Service
              </span>
            </nav>
          </div>

          <div className="max-w-3xl">
            <h1 className="text-foreground mb-4 text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
              Terms of Service
            </h1>
            <p className="text-muted-foreground text-lg leading-relaxed">
              Terms and conditions for using ThaiCopilot. By creating an
              account, you agree to these terms.
            </p>
            {lastUpdated && (
              <div className="mt-6">
                <span className="text-foreground inline-flex w-fit items-center justify-center rounded-full border border-slate-200 px-2 py-0.5 text-xs font-medium whitespace-nowrap dark:border-slate-800">
                  Last updated: {lastUpdated}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="min-h-screen bg-white dark:bg-slate-950">
        <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
          <article className="prose prose-slate dark:prose-invert prose-headings:font-bold prose-headings:tracking-tight prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6 prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-4 prose-p:text-base prose-p:leading-relaxed prose-p:text-slate-700 dark:prose-p:text-slate-300 prose-li:text-base prose-li:leading-relaxed prose-a:text-indigo-600 dark:prose-a:text-indigo-400 prose-a:no-underline hover:prose-a:underline prose-strong:text-slate-900 dark:prose-strong:text-slate-100 prose-strong:font-semibold max-w-none">
            <ContentSection id="acceptance" title="Acceptance of Terms">
              <p>
                Welcome to ThaiCopilot. By accessing or using our platform, you
                agree to be bound by these Terms of Service ("Terms"). If you
                don't agree to these Terms, please don't use ThaiCopilot.
              </p>
              <p>
                These Terms constitute a legally binding agreement between you
                and Rahul Roy ("we," "us," "our"), the operator of ThaiCopilot.
                These Terms apply to all users of the platform.
              </p>
            </ContentSection>

            <ContentSection
              id="service-description"
              title="Service Description"
            >
              <p>
                ThaiCopilot is an AI-powered Thai language learning platform
                designed to help expats and travelers learn practical Thai
                through:
              </p>
              <ul>
                <li>
                  <strong>Conversations:</strong> Practice Thai with AI-powered
                  chat that simulates real interactions
                </li>
                <li>
                  <strong>Flashcards:</strong> Build vocabulary using spaced
                  repetition for effective memorization
                </li>
                <li>
                  <strong>Progress Tracking:</strong> Monitor your learning
                  journey with streaks, statistics, and milestones
                </li>
              </ul>
              <p>
                We're currently in early MVP (Minimum Viable Product) stage,
                gathering feedback from initial users. Features may change,
                expand, or be removed as we iterate based on user needs.
              </p>
            </ContentSection>

            <ContentSection id="eligibility" title="Eligibility">
              <p>To use ThaiCopilot, you must:</p>
              <ol>
                <li>Be at least 13 years old (COPPA compliance)</li>
                <li>Have the legal capacity to enter into binding contracts</li>
                <li>
                  Not be barred from using the service under applicable laws
                </li>
                <li>
                  Provide accurate, current, and complete information during
                  registration
                </li>
              </ol>
              <p>
                If you're under 18, you represent that you have your parent or
                guardian's permission to use ThaiCopilot. Parents/guardians are
                responsible for minors' use of the platform.
              </p>
            </ContentSection>

            <ContentSection id="account" title="Your Account">
              <h3>Account Creation:</h3>
              <p>
                To use ThaiCopilot's features, you must create an account by
                providing your name, email address, and a secure password.
                You're responsible for:
              </p>
              <ul>
                <li>Providing accurate and truthful information</li>
                <li>Maintaining the security of your password</li>
                <li>All activities that occur under your account</li>
                <li>Notifying us immediately of any unauthorized access</li>
              </ul>

              <h3>Account Security:</h3>
              <p>You agree not to:</p>
              <ul>
                <li>Share your account credentials with others</li>
                <li>
                  Create multiple accounts to abuse features or circumvent
                  restrictions
                </li>
                <li>Impersonate any person or entity</li>
                <li>
                  Use automated systems (bots, scrapers) to access the platform
                </li>
              </ul>
            </ContentSection>

            <ContentSection id="acceptable-use" title="Acceptable Use">
              <p>
                You agree to use ThaiCopilot only for lawful purposes and in
                accordance with these Terms. You agree NOT to:
              </p>

              <h3>Prohibited Activities:</h3>
              <ul>
                <li>
                  <strong>Abuse or Harass:</strong> Use offensive, abusive, or
                  harmful content in conversations or flashcards
                </li>
                <li>
                  <strong>Spam:</strong> Send unsolicited messages, spam, or
                  promotional content
                </li>
                <li>
                  <strong>Illegal Activity:</strong> Use the platform for any
                  illegal purpose or to violate any laws
                </li>
                <li>
                  <strong>Reverse Engineer:</strong> Attempt to decompile,
                  disassemble, or reverse engineer the platform
                </li>
                <li>
                  <strong>Security Violations:</strong> Attempt to gain
                  unauthorized access, interfere with operations, or introduce
                  malware
                </li>
                <li>
                  <strong>Data Scraping:</strong> Use automated tools to scrape,
                  extract, or harvest data from the platform
                </li>
                <li>
                  <strong>Impersonation:</strong> Impersonate ThaiCopilot staff,
                  other users, or any person/entity
                </li>
              </ul>

              <p>
                Violation of these rules may result in immediate account
                suspension or termination without refund (when paid features
                launch).
              </p>
            </ContentSection>

            <ContentSection
              id="email-communications"
              title="Email Communications"
            >
              <h3>Transactional Emails:</h3>
              <p>
                By creating an account, you agree to receive transactional
                emails necessary for the service:
              </p>
              <ul>
                <li>Account verification emails</li>
                <li>Password reset requests</li>
                <li>
                  Account security notifications (suspicious activity, settings
                  changes)
                </li>
                <li>
                  Important service updates or changes to Terms/Privacy Policy
                </li>
              </ul>

              <p>
                <strong>
                  You cannot opt out of essential transactional emails
                </strong>{" "}
                as they're required for account security and service
                functionality.
              </p>

              <h3>Marketing Communications:</h3>
              <p>
                We do not currently send marketing emails. If we introduce
                optional newsletters or promotional emails in the future, you'll
                be able to opt out at any time.
              </p>
            </ContentSection>

            <ContentSection
              id="intellectual-property"
              title="Intellectual Property"
            >
              <h3>Your Content:</h3>
              <p>
                You retain ownership of content you create on ThaiCopilot
                (conversations, flashcards, notes). However, by using the
                platform, you grant us a limited, worldwide, royalty-free
                license to:
              </p>
              <ul>
                <li>Store and display your content to provide the service</li>
                <li>
                  Make backups and copies necessary for platform operations
                </li>
                <li>
                  Use anonymized, aggregated data (not linked to you) for
                  analytics and improvements
                </li>
              </ul>

              <h3>Our Content:</h3>
              <p>
                ThaiCopilot's platform, including its design, code, features,
                branding, and content, is protected by copyright, trademark, and
                other intellectual property laws. You may not:
              </p>
              <ul>
                <li>
                  Copy, modify, or create derivative works based on our platform
                </li>
                <li>
                  Use our trademarks, logos, or branding without permission
                </li>
                <li>
                  Remove or obscure any copyright, trademark, or proprietary
                  notices
                </li>
              </ul>
            </ContentSection>

            <ContentSection id="liability" title="Limitation of Liability">
              <p>
                <strong>IMPORTANT:</strong> Please read this section carefully
                as it limits our liability.
              </p>

              <h3>Service Provided "AS-IS":</h3>
              <p>
                ThaiCopilot is provided on an "as-is" and "as-available" basis.
                We make no guarantees about:
              </p>
              <ul>
                <li>
                  Service uptime or availability (we may experience downtime for
                  maintenance or issues)
                </li>
                <li>
                  Language learning outcomes (we can't guarantee you'll become
                  fluent)
                </li>
                <li>
                  Accuracy of AI-generated content (the AI may make mistakes)
                </li>
                <li>Compatibility with all devices or browsers</li>
              </ul>

              <h3>No Liability for Indirect Damages:</h3>
              <p>
                To the maximum extent permitted by law, we are not liable for
                any indirect, incidental, special, consequential, or punitive
                damages, including but not limited to:
              </p>
              <ul>
                <li>Lost profits, revenue, or business opportunities</li>
                <li>Lost or corrupted data</li>
                <li>Costs of substitute services</li>
                <li>
                  Emotional distress or personal injury (except where legally
                  prohibited)
                </li>
              </ul>

              <h3>Maximum Liability:</h3>
              <p>
                Our total liability to you for any claims arising from your use
                of ThaiCopilot shall not exceed:
              </p>
              <ul>
                <li>
                  The amount you paid us in the 12 months before the claim
                  (currently $0 during free beta), OR
                </li>
                <li>$100 USD, whichever is greater</li>
              </ul>

              <p>
                Some jurisdictions don't allow limitations on certain damages,
                so some of these limitations may not apply to you.
              </p>
            </ContentSection>

            <ContentSection id="warranties" title="Disclaimers & Warranties">
              <h3>Language Learning Disclaimer:</h3>
              <p>
                ThaiCopilot is a learning tool, not a guarantee of fluency. Your
                success depends on consistent practice, effort, and real-world
                application. We make no warranties about learning outcomes.
              </p>

              <h3>AI-Generated Content:</h3>
              <p>
                Our AI-powered features may occasionally produce incorrect,
                incomplete, or culturally insensitive content. While we work to
                minimize this, you should verify important information and use
                common sense.
              </p>

              <h3>Third-Party Services:</h3>
              <p>
                We're not responsible for third-party services (Google OAuth,
                analytics providers, etc.) or their availability, security, or
                privacy practices.
              </p>
            </ContentSection>

            <ContentSection id="termination" title="Termination">
              <h3>Your Right to Terminate:</h3>
              <p>
                You can delete your account at any time through Settings →
                Profile → Delete Account. All your data will be permanently
                deleted within 30 days.
              </p>

              <h3>Our Right to Terminate:</h3>
              <p>
                We reserve the right to suspend or terminate your account if
                you:
              </p>
              <ul>
                <li>Violate these Terms or our Privacy Policy</li>
                <li>Engage in fraudulent or illegal activity</li>
                <li>Abuse our platform or harm other users</li>
                <li>
                  Remain inactive for more than 2 years (we'll email you first)
                </li>
              </ul>

              <h3>Effect of Termination:</h3>
              <p>
                Upon termination, your access to ThaiCopilot will immediately
                cease. We'll delete your data per our Privacy Policy, except
                where legally required to retain it. Any paid subscriptions
                (future) are non-refundable after termination for Terms
                violations.
              </p>
            </ContentSection>

            <ContentSection id="disputes" title="Dispute Resolution">
              <h3>Informal Resolution First:</h3>
              <p>
                Before filing any formal claim, please{" "}
                <Link
                  href="/contact"
                  className="text-indigo-600 hover:underline dark:text-indigo-400"
                >
                  contact us
                </Link>{" "}
                to resolve the issue informally. We'll work in good faith to
                find a solution.
              </p>

              <h3>Governing Law:</h3>
              <p>
                These Terms are governed by the laws of India. Any disputes that
                can't be resolved informally shall be subject to the exclusive
                jurisdiction of the courts in India.
              </p>

              <h3>Severability:</h3>
              <p>
                If any provision of these Terms is found unenforceable, the
                remaining provisions continue in full effect.
              </p>
            </ContentSection>

            <ContentSection id="changes" title="Changes to These Terms">
              <p>
                We may update these Terms from time to time to reflect changes
                in our service, legal requirements, or business practices.
              </p>

              <h3>How We'll Notify You:</h3>
              <ul>
                <li>
                  We'll email you at least 14 days before material changes take
                  effect
                </li>
                <li>
                  We'll update the "Last updated" date at the top of this page
                </li>
                <li>
                  For minor clarifications or corrections, we may update without
                  advance notice
                </li>
              </ul>

              <h3>Your Options:</h3>
              <p>
                If you disagree with changes, you must stop using ThaiCopilot
                and delete your account before the changes take effect.
                Continued use after changes means you accept the updated Terms.
              </p>
            </ContentSection>

            <ContentSection id="contact" title="Contact">
              <p>Questions about these Terms? Contact us:</p>

              <div className="not-prose space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-6 dark:border-slate-800 dark:bg-slate-900">
                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  ThaiCopilot Legal
                </p>
                <p className="text-sm text-slate-700 dark:text-slate-300">
                  <Link
                    href="/contact"
                    className="text-indigo-600 hover:underline dark:text-indigo-400"
                  >
                    Contact Us
                  </Link>
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  We aim to respond within 24-48 business hours.
                </p>
              </div>
            </ContentSection>
          </article>
        </div>
      </div>

      <MarketingFooter />
    </>
  )
}
